import React from 'react'

// Generic React error boundary.
//
// Without one of these, any uncaught error thrown while rendering (a bad
// prop, a null ref, a canvas SecurityError bubbling out of an event
// handler-triggered render, etc.) unmounts the whole component tree and
// React just renders nothing - to the user the page silently goes blank,
// with no indication anything went wrong or how to recover. That is
// especially costly on a long form like child registration, where the
// alternative to a friendly "something went wrong, retry" message is the
// parent losing everything they typed and not knowing why.
//
// This only catches errors thrown during rendering / lifecycle methods of
// its subtree (per React's error boundary contract) - it is not a
// substitute for try/catch around async work, which this codebase already
// does around the submit handlers.
class ErrorBoundary extends React.Component {
   constructor(props) {
      super(props)
      this.state = { hasError: false }
   }

   static getDerivedStateFromError() {
      return { hasError: true }
   }

   componentDidCatch(error, info) {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught an error:', error, info)
   }

   handleRetry = () => {
      this.setState({ hasError: false })
   }

   render() {
      if (this.state.hasError) {
         if (this.props.fallback) {
            return this.props.fallback(this.handleRetry)
         }
         return (
            <div className="p-4 text-center">
               <h5 className="mb-2">Something went wrong</h5>
               <p className="text-muted mb-3">
                  {this.props.message || 'This section ran into an unexpected error. Your entered data has not been submitted.'}
               </p>
               <button type="button" className="btn btn-primary" onClick={this.handleRetry}>
                  Try Again
               </button>
            </div>
         )
      }

      return this.props.children
   }
}

export default ErrorBoundary
